import type { BaziChart, BaziPillar } from "@/lib/boide-api";

const PILLAR_ORDER: { key: "year" | "month" | "day" | "hour"; label: string }[] = [
  { key: "year", label: "Năm" },
  { key: "month", label: "Tháng" },
  { key: "day", label: "Ngày" },
  { key: "hour", label: "Giờ" },
];

const ELEMENT_TEXT_COLORS: Record<string, string> = {
  Mộc: "text-emerald-700 dark:text-[#a3fad1]",
  Hỏa: "text-rose-700 dark:text-[#ffb3b3]",
  Thổ: "text-amber-700 dark:text-[#fff0ad]",
  Kim: "text-zinc-700 dark:text-[#f3f4f6]",
  Thủy: "text-sky-700 dark:text-[#bde4ff]",
};

const ELEMENT_GLOWS: Record<string, string> = {
  Mộc: "dark:drop-shadow-[0_0_12px_rgba(16,185,129,0.5)]",
  Hỏa: "dark:drop-shadow-[0_0_12px_rgba(244,63,94,0.5)]",
  Thổ: "dark:drop-shadow-[0_0_12px_rgba(245,158,11,0.5)]",
  Kim: "dark:drop-shadow-[0_0_12px_rgba(255,255,255,0.45)]",
  Thủy: "dark:drop-shadow-[0_0_12px_rgba(14,165,233,0.5)]",
};

const ELEMENT_TAGS: Record<string, string> = {
  Mộc: "border-emerald-600/25 text-emerald-700 bg-emerald-500/10 hover:bg-emerald-500/15 dark:border-emerald-500/20 dark:text-emerald-300 dark:bg-emerald-500/5 dark:hover:bg-emerald-500/10",
  Hỏa: "border-rose-600/25 text-rose-700 bg-rose-500/10 hover:bg-rose-500/15 dark:border-rose-500/20 dark:text-rose-300 dark:bg-rose-500/5 dark:hover:bg-rose-500/10",
  Thổ: "border-amber-600/25 text-amber-700 bg-amber-500/10 hover:bg-amber-500/15 dark:border-amber-500/20 dark:text-amber-300 dark:bg-amber-500/5 dark:hover:bg-amber-500/10",
  Kim: "border-zinc-500/25 text-zinc-700 bg-zinc-500/10 hover:bg-zinc-500/15 dark:border-zinc-500/20 dark:text-zinc-100 dark:bg-zinc-500/5 dark:hover:bg-zinc-500/10",
  Thủy: "border-sky-600/25 text-sky-700 bg-sky-500/10 hover:bg-sky-500/15 dark:border-sky-500/20 dark:text-sky-300 dark:bg-sky-500/5 dark:hover:bg-sky-500/10",
};

const ELEMENT_STRUCTURE: Record<
  string,
  { glyph: string; role: string; color: string; softColor: string }
> = {
  Hỏa: { glyph: "火", role: "KẾT NỐI", color: "#d84b52", softColor: "#ffb3b3" },
  Thổ: { glyph: "土", role: "SÁNG TẠO", color: "#a96624", softColor: "#f4c477" },
  Kim: { glyph: "金", role: "QUẢN LÝ", color: "#858a92", softColor: "#e2e5e9" },
  Thủy: { glyph: "水", role: "HỖ TRỢ", color: "#3678ad", softColor: "#9ed8ff" },
  Mộc: { glyph: "木", role: "TƯ DUY", color: "#16815b", softColor: "#8fe0bd" },
};

function elementTextClass(element: string) {
  return ELEMENT_TEXT_COLORS[element] || "text-[#E5B869]";
}

function elementGlowClass(element: string) {
  return ELEMENT_GLOWS[element] || "";
}

function elementTagClass(element: string) {
  return (
    ELEMENT_TAGS[element] ||
    "border-border/50 text-foreground/80 bg-background/50 dark:border-white/10 dark:text-white/80 dark:bg-white/5"
  );
}

const RADAR_ORDER = ["Hỏa", "Thổ", "Kim", "Thủy", "Mộc"] as const;

function radarPoint(index: number, radius: number, center: { x: number; y: number }) {
  const angle = -Math.PI / 2 + (index * Math.PI * 2) / RADAR_ORDER.length;
  return {
    x: center.x + Math.cos(angle) * radius,
    y: center.y + Math.sin(angle) * radius,
  };
}

function pointsAttribute(points: { x: number; y: number }[]) {
  return points.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ");
}

function formatPercent(value: number) {
  return `${Number.isInteger(value) ? value : value.toFixed(1)}%`;
}

function StructureRadar({
  values,
  compact = false,
}: {
  values: Map<string, number>;
  compact?: boolean;
}) {
  const maxValue = Math.max(...RADAR_ORDER.map((element) => values.get(element) ?? 0), 1);
  const scaleMax = Math.max(50, Math.ceil(maxValue / 10) * 10);
  const center = compact ? { x: 180, y: 148 } : { x: 310, y: 205 };
  const radius = compact ? 88 : 118;
  const dataPoints = RADAR_ORDER.map((element, index) =>
    radarPoint(index, (Math.max(0, values.get(element) ?? 0) / scaleMax) * radius, center),
  );
  const elementPositions = compact
    ? [
        { x: 180, y: 22 },
        { x: 326, y: 106 },
        { x: 272, y: 230 },
        { x: 88, y: 230 },
        { x: 34, y: 106 },
      ]
    : [
        { x: 310, y: 28 },
        { x: 548, y: 142 },
        { x: 470, y: 348 },
        { x: 150, y: 348 },
        { x: 72, y: 142 },
      ];

  return (
    <svg
      viewBox={compact ? "0 0 360 270" : "0 0 620 390"}
      className="block h-auto w-full"
      role="img"
      aria-label={`Biểu đồ cấu trúc Bát Tự: ${RADAR_ORDER.map((element) => `${element} ${formatPercent(values.get(element) ?? 0)}`).join(", ")}`}
    >
      <defs>
        <linearGradient
          id={`bazi-radar-fill-${compact ? "mobile" : "desktop"}`}
          x1="0"
          y1="0"
          x2="1"
          y2="1"
        >
          <stop offset="0%" stopColor="#f1cf8b" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#b8860b" stopOpacity="0.25" />
        </linearGradient>
        <filter
          id={`bazi-radar-shadow-${compact ? "mobile" : "desktop"}`}
          x="-40%"
          y="-40%"
          width="180%"
          height="180%"
        >
          <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodColor="#6f4d16" floodOpacity="0.25" />
        </filter>
      </defs>

      {[1, 2, 3, 4, 5].map((level) => (
        <polygon
          key={level}
          points={pointsAttribute(
            RADAR_ORDER.map((_, index) => radarPoint(index, (radius * level) / 5, center)),
          )}
          fill="none"
          stroke="currentColor"
          strokeWidth={level === 5 ? 1.4 : 0.85}
          className="text-[#a7834b]/45 dark:text-[#E5B869]/24"
        />
      ))}

      {RADAR_ORDER.map((_, index) => {
        const outerPoint = radarPoint(index, radius, center);
        return (
          <line
            key={index}
            x1={center.x}
            y1={center.y}
            x2={outerPoint.x}
            y2={outerPoint.y}
            stroke="currentColor"
            strokeWidth="0.85"
            className="text-[#a7834b]/40 dark:text-[#E5B869]/22"
          />
        );
      })}

      <polygon
        points={pointsAttribute(dataPoints)}
        fill={`url(#bazi-radar-fill-${compact ? "mobile" : "desktop"})`}
        stroke="#b8860b"
        strokeWidth={compact ? 1.8 : 2.2}
        strokeLinejoin="round"
      />

      {dataPoints.map((point, index) => {
        const element = RADAR_ORDER[index];
        const value = values.get(element) ?? 0;
        const pointRadius = Math.hypot(point.x - center.x, point.y - center.y);
        const labelPoint = radarPoint(
          index,
          Math.max(pointRadius + (compact ? 18 : 24), compact ? 34 : 43),
          center,
        );
        const labelWidth = compact ? 39 : 48;
        const labelHeight = compact ? 18 : 21;
        return (
          <g key={element}>
            <circle
              cx={point.x}
              cy={point.y}
              r={compact ? 4 : 5}
              fill="#E5B869"
              stroke="#78530b"
              strokeWidth="1.2"
              filter={`url(#bazi-radar-shadow-${compact ? "mobile" : "desktop"})`}
            />
            <rect
              x={labelPoint.x - labelWidth / 2}
              y={labelPoint.y - labelHeight / 2}
              width={labelWidth}
              height={labelHeight}
              rx={labelHeight / 2}
              className="fill-[#fffaf1] stroke-[#d7bb88] dark:fill-[#17120d] dark:stroke-[#E5B869]/45"
            />
            <text
              x={labelPoint.x}
              y={labelPoint.y + (compact ? 3.5 : 4)}
              textAnchor="middle"
              className={`${compact ? "text-[9px]" : "text-[11px]"} fill-[#5c4630] font-bold dark:fill-[#f5dfb3]`}
            >
              {formatPercent(value)}
            </text>
          </g>
        );
      })}

      {RADAR_ORDER.map((element, index) => {
        const meta = ELEMENT_STRUCTURE[element];
        const position = elementPositions[index];
        const circleRadius = compact ? 13 : 19;
        return (
          <g key={element} transform={`translate(${position.x}, ${position.y})`}>
            <circle
              r={circleRadius}
              fill={meta.color}
              stroke={meta.softColor}
              strokeWidth="1.2"
              opacity="0.92"
            />
            <text
              y={compact ? 5 : 6}
              textAnchor="middle"
              fill="white"
              className={`${compact ? "text-[13px]" : "text-[18px]"} font-serif font-bold`}
            >
              {meta.glyph}
            </text>
            <text
              y={compact ? 32 : 43}
              textAnchor="middle"
              className={`${compact ? "text-[12px]" : "text-[15px]"} fill-foreground font-bold dark:fill-white`}
            >
              {element}
            </text>
            {!compact && (
              <text
                y="59"
                textAnchor="middle"
                className="fill-muted-foreground text-[9px] font-bold tracking-[0.1em] dark:fill-white/50"
              >
                {meta.role}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function ElementStructureChart({ chart }: { chart: BaziChart }) {
  const values = new Map(chart.element_balance.map((item) => [item.ngu_hanh, item.percent]));
  const masterMeta = ELEMENT_STRUCTURE[chart.day_master.ngu_hanh];

  return (
    <section
      data-tour="battu-nguhanh"
      className="min-w-0 overflow-hidden rounded-2xl border border-gold-soft/50 bg-card/85 shadow-mystic backdrop-blur"
    >
      <header className="flex flex-col gap-3 border-b border-gold-soft/30 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div>
          <h2 className="font-serif-soft text-sm font-bold uppercase tracking-[0.15em] text-foreground sm:text-base dark:text-[#f3dfb8]">
            Cấu trúc Bát Tự
          </h2>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Tỷ trọng và xu hướng của Ngũ Hành
          </p>
        </div>
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="size-8 shrink-0 rounded-full border text-center font-serif text-lg font-bold leading-[1.8rem] text-white shadow-sm"
            style={{
              backgroundColor: masterMeta?.color ?? "#9b7426",
              borderColor: masterMeta?.softColor ?? "#E5B869",
            }}
          >
            {masterMeta?.glyph ?? chart.day_master.ngu_hanh.slice(0, 1)}
          </span>
          <div className="min-w-0">
            <div className="truncate text-xs font-semibold text-foreground sm:text-sm">
              Nhật Chủ: {chart.day_master.day_master} {chart.day_master.ngu_hanh}
            </div>
            <div className="truncate text-[10px] font-medium capitalize text-primary sm:text-[11px]">
              {chart.day_master.band}
            </div>
          </div>
        </div>
      </header>

      <div className="hidden px-3 pb-2 pt-3 sm:block sm:px-4">
        <div className="mx-auto w-full max-w-[620px]">
          <StructureRadar values={values} />
        </div>
      </div>

      <div className="px-2.5 pb-3 pt-2 sm:hidden">
        <StructureRadar values={values} compact />
        <div className="mt-1 grid grid-cols-2 gap-1.5">
          {RADAR_ORDER.map((element, index) => {
            const meta = ELEMENT_STRUCTURE[element];
            return (
              <div
                key={element}
                className={`flex min-w-0 items-center gap-2 rounded-lg border border-border/45 bg-background/35 px-2 py-1.5 ${
                  index === RADAR_ORDER.length - 1
                    ? "col-span-2 mx-auto w-[calc(50%-0.1875rem)]"
                    : ""
                }`}
              >
                <span
                  className="size-6 shrink-0 rounded-full text-center font-serif text-sm font-bold leading-6 text-white"
                  style={{ backgroundColor: meta.color }}
                >
                  {meta.glyph}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-[11px] font-semibold text-foreground">
                    {element} · {formatPercent(values.get(element) ?? 0)}
                  </span>
                  <span className="block truncate text-[9px] font-medium tracking-wider text-muted-foreground">
                    {meta.role}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function PillarCard({
  label,
  pillar,
  isDay,
}: {
  label: string;
  pillar: BaziPillar;
  isDay?: boolean;
}) {
  return (
    <div
      className={`relative flex flex-col items-stretch group transition-all duration-300 h-full ${
        isDay ? "scale-[1.04] z-10 my-1.5" : "my-1.5"
      }`}
    >
      {/* Top scroll Cap */}
      <div className="relative flex items-center justify-center z-10 -mb-[2px]">
        <div
          className={`h-2.5 w-[calc(100%+8px)] rounded-full bg-gradient-to-r ${
            isDay
              ? "from-[#b8860b] via-[#E5B869] to-[#b8860b] shadow-[0_0_12px_rgba(229,184,105,0.6)]"
              : "from-stone-300 via-stone-500 to-stone-300 shadow-[0_1px_3px_rgba(92,64,36,0.25)] dark:from-zinc-800 dark:via-zinc-600 dark:to-zinc-800 dark:shadow-[0_1px_3px_rgba(0,0,0,0.5)]"
          }`}
        />
      </div>

      {/* Scroll Paper Body (Deep Cosmic Purple Scroll Background) */}
      <div
        className={`flex flex-col flex-1 px-1.5 py-3 sm:px-4 sm:py-5 border-x ${
          isDay
            ? "bg-gradient-to-b from-amber-50/95 via-stone-50/95 to-amber-100/90 border-x-2 border-x-[#b8860b] shadow-[0_16px_36px_rgba(92,64,36,0.12),inset_0_0_15px_rgba(184,134,11,0.08)] dark:from-[#24231f]/95 dark:via-[#1c1c19]/95 dark:to-[#121211]/98 dark:border-x-[#E5B869] dark:shadow-[0_0_20px_rgba(229,184,105,0.08),inset_0_0_15px_rgba(229,184,105,0.05)]"
            : "bg-gradient-to-b from-stone-50/95 to-amber-50/90 border-x border-x-amber-900/10 shadow-[inset_0_0_10px_rgba(184,134,11,0.05)] dark:from-[#1e1e1b]/95 dark:to-[#121211]/98 dark:border-x-white/10 dark:shadow-[inset_0_0_10px_rgba(255,255,255,0.02)]"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-amber-900/10 pb-2 dark:border-white/5">
          <span className="text-[9px] sm:text-[11px] font-medium uppercase tracking-[0.2em] text-foreground/55 dark:text-white/50">
            {label}
          </span>
          {isDay && (
            <span className="rounded-full border border-[#E5B869]/30 bg-[#E5B869]/5 px-1 sm:px-2 py-0.5 text-[7px] sm:text-[9px] uppercase tracking-wider text-[#E5B869] shadow-[0_0_8px_rgba(229,184,105,0.2)] font-semibold">
              <span className="hidden xs:inline">Nhật chủ</span>
              <span className="xs:hidden">N.Chủ</span>
            </span>
          )}
        </div>

        {/* Can and Chi (stacked vertically) */}
        <div className="flex flex-col items-center justify-center py-2.5 sm:py-5 gap-2 sm:gap-4">
          {/* Can */}
          <div className="flex flex-col items-center">
            <div
              className={`text-[2rem] sm:text-[3.25rem] leading-none font-bold font-garamond ${elementTextClass(pillar.can_ngu_hanh)} ${elementGlowClass(pillar.can_ngu_hanh)}`}
            >
              {pillar.can}
            </div>
            <div className="mt-1 text-[8px] sm:text-[10px] text-foreground/50 tracking-wider dark:text-white/40">
              <span className="hidden sm:inline">
                {pillar.can_ngu_hanh} · {pillar.can_am_duong}
              </span>
              <span className="sm:hidden">{pillar.can_ngu_hanh}</span>
            </div>
          </div>

          {/* Elegant Divider */}
          <div
            className={`w-6 sm:w-12 h-px ${isDay ? "bg-gradient-to-r from-transparent via-[#b8860b]/35 to-transparent dark:via-[#E5B869]/30" : "bg-gradient-to-r from-transparent via-foreground/15 to-transparent dark:via-white/10"}`}
          />

          {/* Chi */}
          <div className="flex flex-col items-center">
            <div
              className={`text-[2rem] sm:text-[3.25rem] leading-none font-bold font-garamond ${elementTextClass(pillar.chi_ngu_hanh)} ${elementGlowClass(pillar.chi_ngu_hanh)}`}
            >
              {pillar.chi}
            </div>
            <div className="mt-1 text-[8px] sm:text-[10px] text-foreground/50 tracking-wider dark:text-white/40">
              <span className="hidden sm:inline">
                {pillar.chi_ngu_hanh} · {pillar.chi_am_duong}
              </span>
              <span className="sm:hidden">{pillar.chi_ngu_hanh}</span>
            </div>
          </div>
        </div>

        {/* Thập thần */}
        <div className="mt-auto pt-2 sm:pt-3 border-t border-amber-900/10 text-xs text-foreground/70 dark:border-white/5 dark:text-white/70">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center text-[8px] sm:text-[10px] text-foreground/50 dark:text-white/40 gap-0.5">
            <span className="truncate">Thập thần Can</span>
            <span className="font-semibold text-[#E5B869]/90 truncate">
              {pillar.thap_than_can || "Nhật Chủ"}
            </span>
          </div>
        </div>

        {/* Hidden Stems (Tàng Can) */}
        <div className="mt-2.5 sm:mt-3">
          <div className="text-[8px] sm:text-[10px] text-foreground/45 uppercase tracking-wider mb-1.5 dark:text-white/30">
            Tàng Can
          </div>
          <div className="flex flex-wrap gap-0.5 sm:gap-1">
            {pillar.tang_can.map((stem) => (
              <span
                key={`${pillar.can_chi}-${stem.can}`}
                className={`border px-1 py-0.5 rounded text-[8px] sm:text-[10px] scale-90 sm:scale-100 origin-left ${elementTagClass(stem.ngu_hanh)}`}
              >
                {stem.can}·{stem.thap_than}
              </span>
            ))}
          </div>
        </div>

        {/* tiet_khi_thang */}
        {pillar.tiet_khi_thang && (
          // Dòng 1: tiết đang chạy lúc sinh (vd Thu Phân — các lịch khác in số này).
          // Dòng 2: tiết KHỞI THÁNG (vd Bạch Lộ) — thứ thật sự quyết định chi tháng.
          <div className="mt-2.5 sm:mt-3 text-[8px] sm:text-[10px] text-foreground/50 text-center bg-background/45 py-0.5 sm:py-1 rounded border border-border/40 dark:text-white/35 dark:bg-white/5 dark:border-white/5">
            <div className="truncate">
              Tiết: {pillar.tiet_khi_hien_tai ?? pillar.tiet_khi_thang}
            </div>
            {pillar.tiet_khi_hien_tai && pillar.tiet_khi_hien_tai !== pillar.tiet_khi_thang ? (
              <div className="truncate opacity-80">
                Tháng từ {pillar.tiet_khi_thang}
                {pillar.tiet_khi_moment
                  ? ` · ${pillar.tiet_khi_moment.slice(11, 16)} ${pillar.tiet_khi_moment.slice(8, 10)}/${pillar.tiet_khi_moment.slice(5, 7)}`
                  : ""}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Bottom scroll Cap */}
      <div className="relative flex items-center justify-center z-10 -mt-[2px]">
        <div
          className={`h-2.5 w-[calc(100%+8px)] rounded-full bg-gradient-to-r ${
            isDay
              ? "from-[#b8860b] via-[#E5B869] to-[#b8860b] shadow-[0_0_12px_rgba(229,184,105,0.6)]"
              : "from-stone-300 via-stone-500 to-stone-300 shadow-[0_-1px_3px_rgba(92,64,36,0.25)] dark:from-zinc-800 dark:via-zinc-600 dark:to-zinc-800 dark:shadow-[0_-1px_3px_rgba(0,0,0,0.5)]"
          }`}
        />
      </div>
    </div>
  );
}

export function BaziBoard({ chart, maskPii = false }: { chart: BaziChart; maskPii?: boolean }) {
  return (
    <div className="space-y-4">
      <div
        data-tour="battu-board"
        className="border border-gold-soft bg-card/90 p-4 rounded-2xl shadow-mystic backdrop-blur"
      >
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5B869]/75 font-semibold">
              Lá số Bát Tự
            </div>
            <h1 className="mt-1 font-serif-soft text-2xl text-foreground font-semibold">
              {maskPii ? "Ẩn danh" : chart.ho_ten}
            </h1>
            <div className="mt-1 text-sm text-muted-foreground">
              {maskPii ? (
                "•••"
              ) : (
                <>
                  {chart.ngay_sinh}/{chart.thang_sinh}/{chart.nam_sinh} ·{" "}
                  {String(chart.gio_sinh).padStart(2, "0")}:
                  {String(chart.phut_sinh).padStart(2, "0")}
                </>
              )}
            </div>
          </div>
          <div className="rounded-xl border border-gold-soft/40 bg-background/45 px-4 py-2.5 backdrop-blur-sm shadow-sm flex flex-col justify-center dark:bg-[#080706]/80">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {chart.day_master.label}
            </div>
            <div className="text-base font-semibold mt-0.5">
              <span
                className={`font-garamond font-bold text-xl ${elementTextClass(chart.day_master.ngu_hanh)} ${elementGlowClass(chart.day_master.ngu_hanh)}`}
              >
                {chart.day_master.day_master}
              </span>
              <span className="text-muted-foreground/60 mx-1.5">·</span>
              <span className={`text-sm ${elementTextClass(chart.day_master.ngu_hanh)}`}>
                {chart.day_master.ngu_hanh}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 auto-rows-fr gap-1 sm:gap-4 pt-2">
        {PILLAR_ORDER.map((item) => (
          <PillarCard
            key={item.key}
            label={item.label}
            pillar={chart.pillars[item.key]}
            isDay={item.key === "day"}
          />
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
        <ElementStructureChart chart={chart} />

        <section
          data-tour="battu-diachi"
          className="border border-gold-soft/50 bg-card/85 backdrop-blur p-4 rounded-2xl shadow-mystic"
        >
          <div className="text-[12px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
            Quan hệ địa chi
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {chart.relations.length ? (
              chart.relations.map((item) => (
                <span
                  key={`${item.type}-${item.label}`}
                  className="border border-border/50 bg-background/45 rounded px-2.5 py-1 text-sm text-foreground/75 transition hover:bg-accent/45 dark:border-white/10 dark:bg-white/5 dark:text-white/72 dark:hover:bg-white/10"
                >
                  {item.label}
                </span>
              ))
            ) : (
              <span className="text-sm text-muted-foreground italic p-1">
                Chưa có hợp/xung nổi bật.
              </span>
            )}
          </div>
        </section>
      </div>

      <section
        data-tour="battu-daivan"
        className="border border-gold-soft/50 bg-card/85 backdrop-blur p-4 rounded-2xl shadow-mystic"
      >
        <div className="text-[12px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
          Đại vận
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="border-b border-border/50 py-2.5 pr-3 font-semibold dark:border-white/10">
                  Vận
                </th>
                <th className="border-b border-border/50 py-2.5 pr-3 font-semibold dark:border-white/10">
                  Can Chi
                </th>
                <th className="border-b border-border/50 py-2.5 pr-3 font-semibold dark:border-white/10">
                  Tuổi
                </th>
                <th className="border-b border-border/50 py-2.5 pr-3 font-semibold dark:border-white/10">
                  Năm bắt đầu
                </th>
                <th className="border-b border-border/50 py-2.5 pr-3 font-semibold dark:border-white/10">
                  Chiều
                </th>
              </tr>
            </thead>
            <tbody className="text-foreground/75 dark:text-white/72">
              {chart.luck_cycles.map((cycle) => (
                <tr
                  key={cycle.so_thu}
                  className="hover:bg-accent/35 transition duration-155 dark:hover:bg-white/5"
                >
                  <td className="border-b border-border/35 py-2.5 pr-3 dark:border-white/5">
                    {cycle.so_thu}
                  </td>
                  <td className="border-b border-border/35 py-2.5 pr-3 text-primary font-medium dark:border-white/5">
                    {cycle.can_chi}
                  </td>
                  <td className="border-b border-border/35 py-2.5 pr-3 dark:border-white/5">
                    {cycle.tuoi_bat_dau}-{cycle.tuoi_ket_thuc}
                  </td>
                  <td className="border-b border-border/35 py-2.5 pr-3 dark:border-white/5">
                    {cycle.nam_bat_dau}
                  </td>
                  <td className="border-b border-border/35 py-2.5 pr-3 dark:border-white/5">
                    {cycle.direction}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
